import { useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";
import { Project } from "../types";

interface ProjectDetailModalProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
}

const ProjectDetailModal = ({ project, isOpen, onClose }: ProjectDetailModalProps) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen || !project) return null;

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case "Beginner":
        return "text-success bg-success/10";
      case "Intermediate":
        return "text-warning bg-warning/10";
      case "Advanced":
        return "text-error bg-error/10";
      default:
        return "text-muted-foreground bg-muted";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="relative w-full max-w-6xl max-h-[90vh] bg-card rounded-lg border border-border shadow-elevation-3 overflow-hidden">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-background/80 backdrop-blur-sm rounded-full hover:bg-background transition-colors duration-300"
        >
          <Icon name="X" size={20} color="var(--color-foreground)" />
        </button>

        <div className="overflow-y-auto max-h-[90vh]">
          <div className="relative h-80 overflow-hidden bg-muted">
            <Image
              src={project.image}
              alt={project.alt}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="flex flex-wrap gap-2 mb-4">
                <span className={`px-3 py-1 rounded-md text-sm font-medium ${getComplexityColor(project.complexity)}`}>
                  {project.complexity}
                </span>
                <span className="px-3 py-1 rounded-md text-sm font-medium text-success bg-success/10">
                  {project.status}
                </span>
                {project.academicProject && (
                  <span className="px-3 py-1 rounded-md text-sm font-medium text-secondary bg-secondary/10 flex items-center gap-1">
                    <Icon name="GraduationCap" size={14} />
                    {project.courseCode || "Academic Project"}
                  </span>
                )}
              </div>
              <h2 className="text-3xl font-bold text-foreground mb-2">{project.title}</h2>
              <p className="text-muted-foreground">{project.description}</p>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="FileText" size={20} color="var(--color-primary)" />
                    Project Overview
                  </h3>
                  <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                    {project.longDescription}
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="AlertCircle" size={20} color="var(--color-warning)" />
                    Challenges Faced
                  </h3>
                  <ul className="space-y-3">
                    {project.challenges.map((challenge, index) => (
                      <li key={index} className="flex gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-warning/10 flex items-center justify-center mt-0.5">
                          <Icon name="AlertTriangle" size={14} color="var(--color-warning)" />
                        </div>
                        <p className="text-muted-foreground">{challenge}</p>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="Lightbulb" size={20} color="var(--color-success)" />
                    Solutions Implemented
                  </h3>
                  <ul className="space-y-3">
                    {project.solutions.map((solution, index) => (
                      <li key={index} className="flex gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-success/10 flex items-center justify-center mt-0.5">
                          <Icon name="CheckCircle2" size={14} color="var(--color-success)" />
                        </div>
                        <p className="text-muted-foreground">{solution}</p>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="BookOpen" size={20} color="var(--color-secondary)" />
                    Key Learnings
                  </h3>
                  <ul className="space-y-3">
                    {project.learnings.map((learning, index) => (
                      <li key={index} className="flex gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-secondary/10 flex items-center justify-center mt-0.5">
                          <Icon name="Sparkles" size={14} color="var(--color-secondary)" />
                        </div>
                        <p className="text-muted-foreground">{learning}</p>
                      </li>
                    ))}
                  </ul>
                </div>

                {project.collaborators && project.collaborators.length > 0 && (
                  <div>
                    <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                      <Icon name="Users" size={20} color="var(--color-accent)" />
                      Collaborators
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {project.collaborators.map((collaborator, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-4 bg-muted rounded-lg"
                        >
                          <Image
                            src={collaborator.avatar}
                            alt={collaborator.alt}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                          <div>
                            <p className="font-semibold text-foreground">{collaborator.name}</p>
                            <p className="text-sm text-muted-foreground">{collaborator.role}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div className="bg-muted rounded-lg p-6">
                  <h3 className="text-lg font-bold text-foreground mb-4">Project Metrics</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Icon name="Code2" size={16} />
                        Lines of Code
                      </span>
                      <span className="font-semibold text-foreground">
                        {project.metrics.linesOfCode.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Icon name="GitCommit" size={16} />
                        Commits
                      </span>
                      <span className="font-semibold text-foreground">
                        {project.metrics.commits}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Icon name="Users" size={16} />
                        Contributors
                      </span>
                      <span className="font-semibold text-foreground">
                        {project.metrics.contributors}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Icon name="Clock" size={16} />
                        Duration
                      </span>
                      <span className="font-semibold text-foreground">
                        {project.metrics.duration}
                      </span>
                    </div>
                    {project.metrics.stars !== undefined && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-2">
                          <Icon name="Star" size={16} />
                          GitHub Stars
                        </span>
                        <span className="font-semibold text-foreground">
                          {project.metrics.stars}
                        </span>
                      </div>
                    )}
                    {project.metrics.forks !== undefined && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-2">
                          <Icon name="GitFork" size={16} />
                          Forks
                        </span>
                        <span className="font-semibold text-foreground">
                          {project.metrics.forks}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-muted rounded-lg p-6">
                  <h3 className="text-lg font-bold text-foreground mb-4">Technologies Used</h3>
                  <div className="space-y-4">
                    {["Frontend", "Backend", "Database", "DevOps", "AI/ML"].map((category) => {
                      const techs = project.technologies.filter(
                        (tech) => tech.category === category
                      );
                      if (techs.length === 0) return null;
                      return (
                        <div key={category}>
                          <p className="text-sm font-medium text-muted-foreground mb-2">
                            {category}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {techs.map((tech, index) => (
                              <span
                                key={index}
                                className="px-3 py-1 bg-background text-foreground rounded-md text-sm font-mono"
                              >
                                {tech.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-muted rounded-lg p-6">
                  <h3 className="text-lg font-bold text-foreground mb-4">Timeline</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" />
                      <span className="text-sm text-muted-foreground">Started:</span>
                      <span className="text-sm font-semibold text-foreground">
                        {new Date(project.startDate).toLocaleDateString("en-US", {
                          month: "long",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                    {project.endDate && (
                      <div className="flex items-center gap-2">
                        <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" />
                        <span className="text-sm text-muted-foreground">Completed:</span>
                        <span className="text-sm font-semibold text-foreground">
                          {new Date(project.endDate).toLocaleDateString("en-US", {
                            month: "long",
                            year: "numeric",
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  {project.githubUrl && (
                    <Button
                      variant="default"
                      size="default"
                      iconName="Github"
                      iconPosition="left"
                      onClick={() => window.open(project.githubUrl, "_blank")}
                      fullWidth
                    >
                      View on GitHub
                    </Button>
                  )}
                  {project.liveUrl && (
                    <Button
                      variant="outline"
                      size="default"
                      iconName="ExternalLink"
                      iconPosition="left"
                      onClick={() => window.open(project.liveUrl, "_blank")}
                      fullWidth
                    >
                      View Live Demo
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailModal;